import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "buttonText", "searchContainer", "searchButtonText", "searchInput", "results", "entityContainer"
  ]

  connect() {
    this.currentEntityType = null
  }

  selectEntity(event) {
    event.preventDefault()

    const entityType = event.currentTarget.dataset.entityType
    const searchPlaceholder = event.currentTarget.dataset.searchPlaceholder
    const entityName = event.currentTarget.textContent.trim()

    // Update the entity button text
    this.buttonTextTarget.textContent = entityName

    // Store current entity type
    this.currentEntityType = entityType

    // Close the entity dropdown menu
    const entityDropdownController = this.application.getControllerForElementAndIdentifier(
      this.entityContainerTarget,
      "dropdown"
    )
    if (entityDropdownController) {
      entityDropdownController.close()
    }

    // Show the search container
    this.searchContainerTarget.classList.remove("hidden")

    // Update search button text and input placeholder
    this.searchButtonTextTarget.textContent = searchPlaceholder
    this.searchInputTarget.placeholder = searchPlaceholder

    // Clear previous search
    this.searchInputTarget.value = ""
    this.resultsTarget.innerHTML = '<div class="px-4 py-3 text-sm text-gray-500">Start typing to search...</div>'

    // Automatically expand the search dropdown
    setTimeout(() => {
      const searchDropdownController = this.application.getControllerForElementAndIdentifier(
        this.searchContainerTarget,
        "dropdown"
      )
      if (searchDropdownController && searchDropdownController.menuTarget) {
        searchDropdownController.menuTarget.classList.remove("hidden")
        document.addEventListener("click", searchDropdownController.boundClose)
        this.searchInputTarget.focus()
      }
    }, 50)
  }

  search(event) {
    const query = event.target.value.trim()

    if (query.length < 2) {
      this.resultsTarget.innerHTML = '<div class="px-4 py-3 text-sm text-gray-500">Start typing to search...</div>'
      return
    }

    this.resultsTarget.innerHTML = '<div class="px-4 py-3 text-sm text-gray-500">Searching...</div>'
    this.performSearch(query, this.currentEntityType)
  }

  performSearch(query, entityType) {
    let searchUrl
    switch(entityType) {
      case 'album':
        searchUrl = `/albums/autocomplete?query=${encodeURIComponent(query)}`
        break
      case 'song':
        searchUrl = `/tracks/search?query=${encodeURIComponent(query)}`
        break
      case 'organization':
        searchUrl = `/organizations/search?query=${encodeURIComponent(query)}`
        break
      default:
        return
    }

    fetch(searchUrl, {
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => response.json())
    .then(data => {
      this.displayResults(data, entityType)
    })
    .catch(error => {
      console.error('Search error:', error)
      this.resultsTarget.innerHTML = '<div class="px-4 py-3 text-sm text-red-500">Error loading results</div>'
    })
  }

  displayResults(data, entityType) {
    if (!data || data.length === 0) {
      this.resultsTarget.innerHTML = '<div class="px-4 py-3 text-sm text-gray-500">No results found</div>'
      return
    }

    let resultsHtml = ''
    data.forEach(item => {
      let resultHtml = ''

      switch(entityType) {
        case 'album':
          const thumbnailUrl = item.cover_image_thumbnail || '/assets/album-placeholder.svg'
          const subtitle = item.upc ? `(${item.upc})` : '(No UPC)'
          resultHtml = `
            <a href="#" class="block px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100" data-action="click->royalties-entity-search#selectResult" data-result-id="${item.id}" data-entity-type="album">
              <div class="flex items-center gap-3">
                <div class="flex-shrink-0">
                  <img src="${thumbnailUrl}"
                       alt="${item.title}"
                       class="w-12 h-12 rounded object-cover"
                       onerror="this.onerror=null; this.src='/assets/album-placeholder.svg';">
                </div>
                <div class="flex-1 min-w-0">
                  <div class="text-sm font-medium text-gray-900 truncate">${item.title}</div>
                  <div class="text-xs text-gray-500">${subtitle}</div>
                </div>
              </div>
            </a>
          `
          break

        case 'song':
          const songThumbnailUrl = item.album_thumbnail || '/assets/album-placeholder.svg'
          const isrc = item.isrc ? `${item.isrc}` : 'No ISRC'
          resultHtml = `
            <a href="#" class="block px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100" data-action="click->royalties-entity-search#selectResult" data-result-id="${item.id}" data-entity-type="song">
              <div class="flex items-center gap-3">
                <div class="flex-shrink-0">
                  <img src="${songThumbnailUrl}"
                       alt="${item.album_title || 'Album'}"
                       class="w-12 h-12 rounded object-cover"
                       onerror="this.onerror=null; this.src='/assets/album-placeholder.svg';">
                </div>
                <div class="flex-1 min-w-0">
                  <div class="text-sm font-medium text-gray-900 truncate">${item.title}</div>
                  <div class="text-xs text-gray-500">${isrc}</div>
                </div>
              </div>
            </a>
          `
          break

        case 'organization':
          const orgType = item.organization_type ? item.organization_type.charAt(0).toUpperCase() + item.organization_type.slice(1) : ''
          const orgRate = item.distribution_rate ? `${item.distribution_rate}% rate` : ''
          const orgDetails = [orgType, orgRate].filter(Boolean).join(' - ')
          resultHtml = `
            <a href="#" class="block px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100" data-action="click->royalties-entity-search#selectResult" data-result-id="${item.id}" data-entity-type="organization">
              <div class="flex items-center gap-3">
                <div class="flex-shrink-0">
                  <div class="w-12 h-12 rounded bg-gray-100 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-gray-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                </div>
                <div class="flex-1 min-w-0">
                  <div class="text-sm font-medium text-gray-900 truncate">${item.title}</div>
                  <div class="text-xs text-gray-500">${orgDetails}</div>
                </div>
              </div>
            </a>
          `
          break
      }

      resultsHtml += resultHtml
    })

    this.resultsTarget.innerHTML = resultsHtml
  }

  selectResult(event) {
    event.preventDefault()

    const resultId = event.currentTarget.dataset.resultId
    const entityType = event.currentTarget.dataset.entityType

    // Build URL preserving existing query parameters
    const currentUrl = new URL(window.location.href)
    const params = new URLSearchParams(currentUrl.search)

    // Remove any existing entity filters before adding new one
    params.delete('album_id')
    params.delete('track_id')
    params.delete('organization_id')

    // Add the new entity filter
    if (entityType === 'album') {
      params.set('album_id', resultId)
    } else if (entityType === 'song') {
      params.set('track_id', resultId)
    } else if (entityType === 'organization') {
      params.set('organization_id', resultId)
    }

    const url = `/royalties?${params.toString()}`
    Turbo.visit(url)
  }
}
